import { useContext, useRef, useState } from 'react';
import I18n from 'I18n';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { getTextContentFromHtml } from 'sanitize-text/sanitizers/TextSanitizer';
import { useRequestAccessMutation } from '../../../../__generated__/chirp/com/hubspot/permissionsmanagement/accessrequest/rpc/AccessRequestRpc.hooks';
import { AccessCheckAccessInfoContext } from '../../../contexts/AccessCheckAccessInfoContext';
import { useAccessCheckTracking } from '../../../contexts/AccessCheckTrackingContext';
import { RequestAccessType } from '../../../constants/requestAccessType';
import { useAdmins } from '../../../hooks/useAdmins';
import { useRequestAccessType } from '../../../hooks/useRequestAccessType';
import { RequestAccessModal } from './RequestAccessModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function RequestAccessTrigger({
  requestAccessConfig,
  taskDescription,
  onRequestAccessSuccess,
  children
}) {
  var _requestAccessConfig$10;
  const [isOpen, setIsOpen] = useState(false);
  const requestAccessType = useRequestAccessType();
  const {
    admins
  } = useAdmins();
  const {
    missingScopes = []
  } = useContext(AccessCheckAccessInfoContext);
  const [requestAccess] = useRequestAccessMutation();
  const track = useAccessCheckTracking();
  const hasSubmittedRef = useRef(false);
  function handleRequestAccess(selectedAdminUserIds, additionalDetails) {
    var _requestAccessConfig$, _requestAccessConfig$2, _requestAccessConfig$3, _requestAccessConfig$4, _requestAccessConfig$5;
    hasSubmittedRef.current = true;
    track('Settings Ui', 'Permission Request Access Submitted', {
      taskName: taskDescription !== null && taskDescription !== void 0 ? taskDescription : '',
      locationKey: (_requestAccessConfig$ = requestAccessConfig === null || requestAccessConfig === void 0 || (_requestAccessConfig$2 = requestAccessConfig.appLocation) === null || _requestAccessConfig$2 === void 0 ? void 0 : _requestAccessConfig$2.id) !== null && _requestAccessConfig$ !== void 0 ? _requestAccessConfig$ : '',
      eventLocation: (_requestAccessConfig$3 = requestAccessConfig === null || requestAccessConfig === void 0 || (_requestAccessConfig$4 = requestAccessConfig.appLocation) === null || _requestAccessConfig$4 === void 0 ? void 0 : _requestAccessConfig$4.label) !== null && _requestAccessConfig$3 !== void 0 ? _requestAccessConfig$3 : ''
    });
    if (requestAccessType === RequestAccessType.SEAT) {
      console.error('RequestAccessTrigger: SEAT request access is not configured yet.');
      FloatingAlertStore.addAlert({
        type: 'danger',
        titleText: I18n.text('access-check-lib.requestAccessModal.errorAlert.title'),
        message: I18n.text('access-check-lib.requestAccessModal.errorAlert.message')
      });
      setIsOpen(false);
      return;
    }
    const selectedUserId = selectedAdminUserIds[0];
    const selectedAdmin = admins.find(admin => admin.userId === selectedUserId);
    const sanitizedDetails = getTextContentFromHtml(additionalDetails);
    requestAccess({
      variables: {
        scopesRequested: missingScopes,
        usersToNotifyForAccess: [Number(selectedUserId)],
        appLocation: requestAccessConfig ? requestAccessConfig.appLocation : undefined,
        customMessage: sanitizedDetails,
        requireAnyScope: (_requestAccessConfig$5 = requestAccessConfig === null || requestAccessConfig === void 0 ? void 0 : requestAccessConfig.requireAnyScope) !== null && _requestAccessConfig$5 !== void 0 ? _requestAccessConfig$5 : false
      }
    }).then(() => {
      FloatingAlertStore.addAlert({
        type: 'success',
        titleText: I18n.text('access-check-lib.requestAccessModal.successAlert.title'),
        message: selectedAdmin ? I18n.text('access-check-lib.requestAccessModal.successAlert.message', {
          adminName: selectedAdmin.name,
          adminEmail: selectedAdmin.email
        }) : undefined
      });
      setIsOpen(false);
      onRequestAccessSuccess === null || onRequestAccessSuccess === void 0 || onRequestAccessSuccess();
    }).catch(error => {
      console.error('RequestAccessTrigger: failed to send access request', error);
      FloatingAlertStore.addAlert({
        type: 'danger',
        titleText: I18n.text('access-check-lib.requestAccessModal.errorAlert.title'),
        message: I18n.text('access-check-lib.requestAccessModal.errorAlert.message')
      });
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [children(() => {
      var _requestAccessConfig$6, _requestAccessConfig$7, _requestAccessConfig$8, _requestAccessConfig$9;
      hasSubmittedRef.current = false;
      track('Settings Ui', 'Request Access Modal Opened', {
        taskName: taskDescription !== null && taskDescription !== void 0 ? taskDescription : '',
        locationKey: (_requestAccessConfig$6 = requestAccessConfig === null || requestAccessConfig === void 0 || (_requestAccessConfig$7 = requestAccessConfig.appLocation) === null || _requestAccessConfig$7 === void 0 ? void 0 : _requestAccessConfig$7.id) !== null && _requestAccessConfig$6 !== void 0 ? _requestAccessConfig$6 : '',
        eventLocation: (_requestAccessConfig$8 = requestAccessConfig === null || requestAccessConfig === void 0 || (_requestAccessConfig$9 = requestAccessConfig.appLocation) === null || _requestAccessConfig$9 === void 0 ? void 0 : _requestAccessConfig$9.label) !== null && _requestAccessConfig$8 !== void 0 ? _requestAccessConfig$8 : ''
      });
      setIsOpen(true);
    }), isOpen && /*#__PURE__*/_jsx("div", {
      onKeyDown: e => e.stopPropagation(),
      onClick: e => e.stopPropagation(),
      onMouseDown: e => e.stopPropagation(),
      onPointerDown: e => e.stopPropagation(),
      children: /*#__PURE__*/_jsx(RequestAccessModal, {
        requestAccessType: requestAccessType,
        appLocationLabel: requestAccessConfig === null || requestAccessConfig === void 0 || (_requestAccessConfig$10 = requestAccessConfig.appLocation) === null || _requestAccessConfig$10 === void 0 ? void 0 : _requestAccessConfig$10.label,
        onRequestAccess: handleRequestAccess,
        onClose: () => {
          if (!hasSubmittedRef.current) {
            var _requestAccessConfig$11, _requestAccessConfig$12, _requestAccessConfig$13, _requestAccessConfig$14;
            track('Settings Ui', 'Permission Request Access Canceled', {
              taskName: taskDescription !== null && taskDescription !== void 0 ? taskDescription : '',
              locationKey: (_requestAccessConfig$11 = requestAccessConfig === null || requestAccessConfig === void 0 || (_requestAccessConfig$12 = requestAccessConfig.appLocation) === null || _requestAccessConfig$12 === void 0 ? void 0 : _requestAccessConfig$12.id) !== null && _requestAccessConfig$11 !== void 0 ? _requestAccessConfig$11 : '',
              eventLocation: (_requestAccessConfig$13 = requestAccessConfig === null || requestAccessConfig === void 0 || (_requestAccessConfig$14 = requestAccessConfig.appLocation) === null || _requestAccessConfig$14 === void 0 ? void 0 : _requestAccessConfig$14.label) !== null && _requestAccessConfig$13 !== void 0 ? _requestAccessConfig$13 : ''
            });
          }
          setIsOpen(false);
        }
      })
    })]
  });
}